import { DEPLOYMENT_ENVIRONMENT, DeploymentEnvironment } from 'env/environments';
import { getHomeLoansDashboardRuntimeConfig } from 'src/services/config/getHomeLoansDashboardRuntimeConfig';
import { MACRuntimeConfig } from 'env/types';
import { getZillowPath } from './getZillowPath';
import { getIntegrationZillowOriginForDeploymentEnvironment } from './getIntegrationZillowOriginForDeploymentEnvironment';
import { getURLSearchParams } from './getURLSearchParams';
import { getLoanIDFromURL } from './getLoanIDFromURL';

const ENV_TO_TOP_LEVEL_DOMAIN: Record<DeploymentEnvironment, string> = {
    [DEPLOYMENT_ENVIRONMENT.DEVELOPMENT]: '.net',
    [DEPLOYMENT_ENVIRONMENT.LOCAL]: `.net`,
    [DEPLOYMENT_ENVIRONMENT.STAGING]: '.net',
    [DEPLOYMENT_ENVIRONMENT.PRODUCTION]: '.com',
};

const ENDS_WITH_DASHBOARD_AND_SLASH = new RegExp(/\/dashboard\/$/gi);

const stripLoanIDQueryParameterFromRelativePath = (path: string): string => {
    const queryParams = getURLSearchParams(path);
    queryParams.delete('loanID');
    const [pathWithoutQueryParams] = path.split('?');
    // I would use URLSearchParams.size here but for some reason it is undefined
    if (!Array.from(queryParams.entries()).length) {
        return pathWithoutQueryParams;
    }
    return `${pathWithoutQueryParams}?${queryParams.toString()}`;
};

const injectLoanIDAsURLParameterToRelativePath = (path: string, loanID: string) => {
    const [actualPath, queryParamsIfTheyExist] = path.split('?');
    if (actualPath.match(ENDS_WITH_DASHBOARD_AND_SLASH)) {
        return `${actualPath}${loanID}/${queryParamsIfTheyExist ? `?${queryParamsIfTheyExist}` : ''}`;
    }
    return `${actualPath}/${loanID}/${queryParamsIfTheyExist ? `?${queryParamsIfTheyExist}` : ''}`;
};

const RELATIVE_PATH_DEPLOYMENT_ENVIRONMENTS: Partial<DeploymentEnvironment>[] = [
    DEPLOYMENT_ENVIRONMENT.LOCAL,
    // To support both dev environment and MRs
    DEPLOYMENT_ENVIRONMENT.DEVELOPMENT,
];

export const _getAppropriateFullDashboardURLForEnvironment = (
    url: string,
    { publicRuntimeConfig }: MACRuntimeConfig,
    { useLocal = false } = {}
) => {
    const { ENV } = publicRuntimeConfig;
    const topLevelDomain = ENV_TO_TOP_LEVEL_DOMAIN[ENV];
    const isURLFullyResolved = url.includes(topLevelDomain);
    const relativePath = isURLFullyResolved ? url.split(topLevelDomain)[1] : url;
    const { loanID, isQueryParameter } = getLoanIDFromURL(relativePath);
    const sanitizedRelativePath = stripLoanIDQueryParameterFromRelativePath(relativePath);
    /**
     * This is one of the few places we should use getZillowPath
     */
    // eslint-disable-next-line no-restricted-syntax
    const guaranteedZillowRelativePath = getZillowPath(sanitizedRelativePath);
    const guaranteedZillowRelativePathWithURLParameterIfAppropriate =
        isQueryParameter && loanID
            ? injectLoanIDAsURLParameterToRelativePath(guaranteedZillowRelativePath, loanID)
            : guaranteedZillowRelativePath;
    if (RELATIVE_PATH_DEPLOYMENT_ENVIRONMENTS.includes(ENV) || useLocal) {
        return guaranteedZillowRelativePathWithURLParameterIfAppropriate;
    }
    const zillowDomain = getIntegrationZillowOriginForDeploymentEnvironment();
    return `${zillowDomain}${guaranteedZillowRelativePathWithURLParameterIfAppropriate}`;
};

/**
 * Co-effects: NextJS deployment environment
 *
 * Formats a dashboard URL received from the graph to be absolute or relative depending on deployment environment,
 * and swaps query parameters for URL parameters. You should use this whenever you are redirecting a user since it
 * guarantees that pages that are deployed on ZHL still end up redirecting to Zillow as expected.
 */
export const getEnvironmentAppropriateFullDashboardURL = (url: string, { useLocal = false } = {}) =>
    _getAppropriateFullDashboardURLForEnvironment(url, getHomeLoansDashboardRuntimeConfig(), { useLocal });
